import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, CircularProgress, Alert, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Stack, TextField, MenuItem, Chip, Tooltip, Button, Divider
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../services/api";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

const pricingIntentUi = (value) => {
  const v = String(value || "").toLowerCase();
  if (v === "yes") return { label: "Would Pay", color: "success" };
  if (v === "no") return { label: "Would Not Pay", color: "error" };
  if (v === "maybe") return { label: "Maybe", color: "warning" };
  return { label: "-", color: "default" };
};

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [isGuest, setIsGuest] = useState("");
  const selectedPricingMeta = pricingIntentUi(selected?.pricing_intent);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await api.feedback.adminList({
        category: category || undefined,
        q: q || undefined,
        is_guest: isGuest || undefined,
        limit: 50,
        page: 1,
      });
      setItems(res.data?.items || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={900}>Feedback</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="bug">Bug</MenuItem>
            <MenuItem value="idea">Idea</MenuItem>
            <MenuItem value="confusion">Confusion</MenuItem>
            <MenuItem value="pricing">Pricing</MenuItem>
          </TextField>

          <TextField
            select
            label="Guest"
            value={isGuest}
            onChange={(e) => setIsGuest(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Guest</MenuItem>
            <MenuItem value="false">Logged in</MenuItem>
          </TextField>

          <TextField
            label="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            fullWidth
          />

          <Button variant="contained" onClick={load}>Apply</Button>
        </Stack>
      </Paper>

      {loading && <CircularProgress />}
      {err && <Alert severity="error">{err}</Alert>}

      {!loading && !err && (
        <Paper sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>ID</b></TableCell>
                  <TableCell><b>Date</b></TableCell>
                  <TableCell><b>User</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell><b>View</b></TableCell>
                  <TableCell><b>Category</b></TableCell>
                  <TableCell><b>Pricing Intent</b></TableCell>
                  <TableCell><b>Message</b></TableCell>
                  <TableCell><b>Page</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((r) => {
                  const pricingMeta = pricingIntentUi(r.pricing_intent);
                  return (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "-"}
                    </TableCell>
                    <TableCell>{r.is_guest ? "Guest" : (r.user_id ?? "-")}</TableCell>
                    <TableCell>
                      {r.email || (r.is_guest ? "-" : "User")}
                    </TableCell>

                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setSelected(r); setOpen(true); }}
                      >
                        Open
                      </Button>
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label={r.category} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={pricingMeta.label} color={pricingMeta.color} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 520, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.message}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.page_url || "-"}
                    </TableCell>
                  </TableRow>
                  );
                })}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary">No feedback yet.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Feedback #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography><b>Category:</b> {selected?.category}</Typography>
            <Typography>
              <b>Pricing Intent:</b>{" "}
              <Chip
                size="small"
                label={selectedPricingMeta.label}
                color={selectedPricingMeta.color}
              />
            </Typography>
            <Typography><b>User:</b> {selected?.is_guest ? "Guest" : (selected?.user_id ?? "-")}</Typography>
            <Typography><b>Email:</b> {selected?.email || "-"}</Typography>
            <Typography><b>Page:</b> {selected?.page_url || "-"}</Typography>
            <Divider />
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {selected?.message || ""}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
